import React, { CSSProperties, useEffect, useRef } from 'react';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';

type ConfettiProps = {
  duration?: number; // how long confetti falls (seconds)
  particleCount?: number;
};

const COLORS = [
  '#FFD700',
  '#FFA500',
  '#FF6347',
  '#FF4500',
  '#00CED1',
  '#7B68EE',
  '#32CD32',
  '#FF69B4',
  '#E98C26',
  '#ffffff',
  '#87CEEB',
  '#98FB98'
];

const SHAPES = ['square', 'rectangle', 'circle'] as const;

const Confetti = ({ duration = 4, particleCount = 60 }: ConfettiProps) => {
  const { reduceAnimations } = useAnimationPreferences();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    if (reduceAnimations) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Size to the TABLE this confetti belongs to, not the window.
    //
    // The canvas is absolutely positioned inside Game.tsx's pageShell (which is
    // `position: relative`), so its box is that one table's felt. In
    // single-table view the shell fills the page and this is the window, as
    // before; in the multi-table grid — where several felts share the viewport —
    // it keeps the celebration on the table that actually won instead of
    // spraying every table on screen. Falls back to the window if the element is
    // not laid out yet (jsdom, or a first frame before layout).
    const box = canvas.parentElement;
    canvas.width = box?.clientWidth || window.innerWidth;
    canvas.height = box?.clientHeight || window.innerHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    type Piece = {
      x: number;
      y: number;
      vx: number;
      vy: number;
      w: number;
      h: number;
      color: string;
      shape: (typeof SHAPES)[number];
      rotation: number;
      rotSpeed: number;
      wobble: number;
      wobbleSpeed: number;
      opacity: number;
    };

    const pieces: Piece[] = [];

    // Spawn confetti from top, spread across width
    for (let i = 0; i < particleCount; i++) {
      const color = COLORS[Math.floor(Math.random() * COLORS.length)];
      const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];

      pieces.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * 200,
        vx: (Math.random() - 0.5) * 3,
        vy: 2 + Math.random() * 4,
        w: shape === 'rectangle' ? 4 + Math.random() * 4 : 5 + Math.random() * 5,
        h: shape === 'rectangle' ? 8 + Math.random() * 8 : 5 + Math.random() * 5,
        color,
        shape,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 10,
        wobble: Math.random() * 10,
        wobbleSpeed: 0.05 + Math.random() * 0.1,
        opacity: 0.8 + Math.random() * 0.2
      });
    }

    const startTime = Date.now();

    const update = () => {
      const elapsed = (Date.now() - startTime) / 1000;
      if (elapsed > duration + 2) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Fade out in last second
      const globalAlpha = elapsed > duration ? Math.max(0, 1 - (elapsed - duration)) : 1;

      for (const p of pieces) {
        p.x += p.vx + Math.sin(p.wobble) * 0.5;
        p.y += p.vy;
        p.rotation += p.rotSpeed;
        p.wobble += p.wobbleSpeed;
        p.vy += 0.02; // gravity
        p.vx *= 0.999; // air resistance

        if (p.y > canvas.height + 20) continue;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity * globalAlpha;
        ctx.fillStyle = p.color;

        if (p.shape === 'circle') {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }

        ctx.restore();
      }

      animRef.current = requestAnimationFrame(update);
    };

    animRef.current = requestAnimationFrame(update);

    return () => cancelAnimationFrame(animRef.current);
  }, [duration, particleCount, reduceAnimations]);

  return reduceAnimations ? null : <canvas ref={canvasRef} style={canvasStyle} />;
};

export default Confetti;

/**
 * ABSOLUTE, not fixed — the canvas covers its own table, not the viewport.
 *
 * `fixed` made the burst viewport-sized wherever it was mounted, which is
 * indistinguishable from correct in single-table view (the felt IS the
 * viewport) but wrong in the multi-table grid: a win at one cell painted
 * confetti across every table on screen, and two simultaneous wins stacked two
 * full-screen canvases. Anchored to Game.tsx's pageShell instead, each table
 * celebrates inside its own bounds — and the shell's `overflow: hidden` keeps
 * stray particles out of the neighbouring felts.
 *
 * pageShell is `position: relative` with `z-index: auto`, so it is NOT a
 * stacking context: this canvas still resolves against the same root context it
 * did as `fixed`, and its z-order against page-level modals is unchanged.
 */
const canvasStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  zIndex: 100
};
